import {
  DimensionSide,
  ElementState,
  ElementType,
  LineType,
  Point,
} from '@ritabygglov/core';
import { useCallback } from 'react';
import { CreateCommand } from '../../../commands/CreateCommand';
import { CreatePolylineCommand } from '../../../commands/CreatePolylineCommand';
import { UpdatePolylineCommand } from '../../../commands/UpdatePolylineCommand';
import { copy } from '../../../helpers/utility';
import { createNewElement } from '../../../services/Actions';
import { presetRegistry } from '../../../services/presetRegistry';
import { CommandManager } from '../../../commands/CommandManager';
import CanvasSession from '../../../services/CanvasSession';
import {
  AppState,
  ClientCoordinates,
  Coordinate,
  LineSegment,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
  ViewBox,
} from '../../../types';
import { applySelfSnap, findOverlappingPoint, isPointSame } from '../helpers';
import { useSnapping } from './useSnapping';

export interface ElementCreationDeps {
  appState: AppState;
  session: CanvasSession;
  commandManager: CommandManager;
  selectedElements: string[];
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  selectionCoordinates: SelectionCoordinates;
  setSelectionCoordinates: React.Dispatch<
    React.SetStateAction<SelectionCoordinates>
  >;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
  viewBox: ViewBox;
  isShiftPressed: boolean;
  isMetaPressed: boolean;
  setSnapLines: React.Dispatch<React.SetStateAction<LineSegment[]>>;
  setSnapPoints: React.Dispatch<React.SetStateAction<Coordinate[]>>;
}

export const useElementCreation = (hooks: ElementCreationDeps) => {
  const {
    appState,
    session,
    selectedElements,
    setSelectedElements,
    selectionCoordinates,
    setSelectionCoordinates,
    selectionMode,
    setSelectionMode,
    viewBox,
    isShiftPressed,
    isMetaPressed,
    setSnapLines,
    setSnapPoints,
    commandManager,
  } = hooks;

  const {
    snapToElements,
    snapPolylinePoint,
    matchesAtPoint,
    applySnapping,
    clearSnapping,
  } = useSnapping(
    setSnapLines,
    setSnapPoints,
    isShiftPressed,
    isMetaPressed,
    viewBox.scale
  );

  const onStartAddElement = useCallback(
    (clientCoordinates: ClientCoordinates, generateUuid: () => string) => {
      const initialX = clientCoordinates.x;
      const initialY = clientCoordinates.y;

      const snapCursor = snapToElements({
        x: initialX,
        y: initialY,
        elements: appState.elements,
        element: undefined,
        excludeSelf: true,
      });

      setSelectionCoordinates({
        ...selectionCoordinates,
        initialX: snapCursor.dx,
        initialY: snapCursor.dy,
      });

      const renderingOrder = Object.keys(appState.elements).length + 1;

      const updatedClientCoordinates = {
        x: snapCursor.dx,
        y: snapCursor.dy,
      };

      switch (selectionMode.elementType) {
        case ElementType.Group: {
          if (!selectionMode.presetType) break;

          const { group, elements } = presetRegistry[
            selectionMode.presetType
          ].build({
            x: updatedClientCoordinates.x,
            y: updatedClientCoordinates.y,
            renderingOrder,
            userId: session.currentUserId,
            generateUuid,
          });

          setSelectedElements([...selectedElements, group.id]);

          commandManager.executeCommand(
            new CreateCommand(elements, session.currentUserId)
          );

          break;
        }
        case ElementType.Rect:
        case ElementType.Text:
        case ElementType.Symbol:
        case ElementType.Fixture:
        case ElementType.Triangle:
        case ElementType.Trapezoid:
        case ElementType.Angle: {
          const id = generateUuid();
          setSelectedElements([...selectedElements, id]);

          const element = createNewElement(
            selectionMode.elementType,
            updatedClientCoordinates.x,
            updatedClientCoordinates.y,
            renderingOrder,
            id,
            session.currentUserId,
            selectionMode.symbolType,
            selectionMode.fixtureType
          );

          if (!element) {
            throw new Error('Failed to create new element');
          }

          const createCommand = new CreateCommand(
            [element],
            session.currentUserId
          );

          commandManager.executeCommand(createCommand);

          break;
        }
        case ElementType.Polyline: {
          const selectedElement = selectedElements[0];

          if (selectedElement) {
            const creationElement = copy(appState.elements[selectedElement]);
            if (!creationElement) break;
            if (creationElement.type !== ElementType.Polyline) {
              throw new Error(
                'Expected selected element to be a polyline element'
              );
            }

            /**
             * If the user starts creating a polyline and clicks the starting point again
             * without triggering an mouse update, the polyline is closed.
             */
            const shouldClose =
              creationElement.points.length ===
              selectionCoordinates.nextPointIndex;

            const overlapsEarlierPoint =
              !isMetaPressed &&
              findOverlappingPoint(
                creationElement.points.length - 1,
                creationElement.points
              ) !== -1;

            if (
              overlapsEarlierPoint ||
              creationElement.lineType === LineType.Dimension ||
              shouldClose
            ) {
              setSelectionMode({
                ...selectionMode,
                type: SelectionModes.None,
              });
            }

            const command = new UpdatePolylineCommand(
              creationElement,
              { state: ElementState.Creation },
              session.currentUserId,
              false
            );
            commandManager.executeCommand(command);

            setSelectionCoordinates({
              ...selectionCoordinates,
              nextPointIndex: selectionCoordinates.nextPointIndex + 1,
            });
          } else {
            const firstPoint: Point = {
              x: clientCoordinates.x,
              y: clientCoordinates.y,
              dimensionSide: DimensionSide.Below,
            };

            const polylineCommand = new CreatePolylineCommand(
              firstPoint,
              selectionMode.lineType!,
              renderingOrder,
              session.currentUserId
            );

            commandManager.executeCommand(polylineCommand);

            setSelectedElements([
              ...selectedElements,
              polylineCommand.getElementId(),
            ]);
            setSelectionCoordinates({
              ...selectionCoordinates,
              nextPointIndex: 1,
            });
          }
          break;
        }
      }
    },
    [
      setSelectionCoordinates,
      selectionCoordinates,
      appState.elements,
      selectionMode,
      setSelectedElements,
      selectedElements,
      session,
      commandManager,
      snapToElements,
      setSelectionMode,
      isMetaPressed,
    ]
  );

  const onUpdateAddElement = useCallback(
    (clientCoordinates: ClientCoordinates, isEphemeral: boolean) => {
      const { x, y } = clientCoordinates;

      //snapping the hovering cursor before element is created
      if (selectedElements.length === 0) {
        const snapCursor = snapToElements({
          x,
          y,
          elements: appState.elements,
          element: undefined,
          excludeSelf: true,
        });

        applySnapping(snapCursor);
      } else {
        clearSnapping();
      }

      const selectedElement = selectedElements[0];
      if (!selectedElement) return;

      const newAppState = Object.assign({}, appState);
      const creationElement = copy(newAppState.elements[selectedElement]);
      if (!creationElement) return;

      switch (creationElement.type) {
        case ElementType.Rect:
        case ElementType.Symbol:
        case ElementType.Fixture:
        case ElementType.Triangle:
        case ElementType.Trapezoid:
        case ElementType.Angle: {
          break;
        }
        case ElementType.Polyline: {
          const endPoint: Point = {
            x,
            y,
            dimensionSide: DimensionSide.Below,
          };
          const points = [...creationElement.points];
          const prevPoint = points[selectionCoordinates.nextPointIndex - 1];

          const snap = snapToElements({
            x,
            y,
            element: creationElement,
            elements: appState.elements,
            excludeSelf: true,
          });

          let { matchedLines, matchedPoints } = snap;
          const { dx: snappedDx, dy: snappedDy } = snap;
          endPoint.x = snappedDx;
          endPoint.y = snappedDy;

          const { point: snappedPoint, shouldHideSnap } = snapPolylinePoint(
            points,
            endPoint,
            selectionCoordinates.nextPointIndex
          );

          if (shouldHideSnap) {
            endPoint.x = snappedPoint.x;
            endPoint.y = snappedPoint.y;
            const rematched = matchesAtPoint(
              snappedPoint,
              appState.elements,
              creationElement.id
            );
            matchedLines = rematched.matchedLines;
            matchedPoints = rematched.matchedPoints;
          }

          applySnapping({
            dx: endPoint.x,
            dy: endPoint.y,
            matchedLines,
            matchedPoints,
          });

          const isPointsSame = isPointSame(prevPoint, endPoint);
          if (!isPointsSame) {
            points[selectionCoordinates.nextPointIndex] = endPoint;
          }

          applySelfSnap(points.length - 1, points, !isMetaPressed);

          const command = new UpdatePolylineCommand(
            creationElement,
            { points, state: ElementState.Creation },
            session.currentUserId,
            isEphemeral
          );
          commandManager.executeCommand(command);
          break;
        }
      }
    },
    [
      selectedElements,
      appState,
      snapToElements,
      matchesAtPoint,
      applySnapping,
      clearSnapping,
      selectionCoordinates.nextPointIndex,
      snapPolylinePoint,
      session.currentUserId,
      commandManager,
      isMetaPressed,
    ]
  );

  const onAddFinishElement = useCallback(() => {
    const selectedElement = selectedElements[0];
    if (!selectedElement) return;
    const newAppState = Object.assign({}, appState);
    const creationElement = copy(newAppState.elements[selectedElement]);
    if (!creationElement) return;

    switch (selectionMode.elementType) {
      case ElementType.Rect:
      case ElementType.Text:
      case ElementType.Symbol:
      case ElementType.Fixture:
      case ElementType.Triangle:
      case ElementType.Trapezoid:
      case ElementType.Angle: {
        // With simplified commands, execution is immediate - no need to complete
        break;
      }
      case ElementType.Polyline: {
        // Polyline operation is completed in MouseUp handler if the last point overlaps
        // or the escape key is pressed

        if (creationElement.type !== ElementType.Polyline) {
          throw new Error('Expected selected element to be a polyline element');
        }

        const command = new UpdatePolylineCommand(
          creationElement,
          {},
          session.currentUserId,
          false
        );
        commandManager.executeCommand(command);
        break;
      }
    }
  }, [
    appState,
    commandManager,
    session.currentUserId,
    selectedElements,
    selectionMode.elementType,
  ]);

  return {
    onStartAddElement,
    onUpdateAddElement,
    onAddFinishElement,
  };
};
